import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useSessionTimeoutWarning } from 'contexts/ui/useSessionTimeoutWarning';

import {
  SESSION_TIMEOUT_COLOR,
  SessionTimeoutMessage,
} from './SessionTimeoutMessage';

import type { SessionTimeoutWarning } from 'contexts/ui/useSessionTimeoutWarning';

export const SESSION_TIMEOUT_NOTIFICATION_ID = 'session-timeout';

const hideWarning = (): void => {
  notifications.hide(SESSION_TIMEOUT_NOTIFICATION_ID);
};

/**
 * Warns a Compete 2.0 user that their session is about to time out, with a countdown that
 * updates on every tick. useSessionTimeoutWarning owns the countdown and the sign-out. The user
 * cannot close the warning: it goes away when they stay connected or when they are signed out.
 *
 * Compete 2.0 pages have no AppUIProvider, so this is the Mantine counterpart of the
 * SessionTimeoutNotifier in contexts/ui.
 */
export function SessionTimeoutNotifier(): null {
  const { t } = useTranslation(['Session']);

  useSessionTimeoutWarning({
    onWarn: (warning: SessionTimeoutWarning) => {
      const notification = {
        id: SESSION_TIMEOUT_NOTIFICATION_ID,
        title: t('Session:timeout.title'),
        color: SESSION_TIMEOUT_COLOR,
        message: <SessionTimeoutMessage {...warning} />,
        autoClose: false as const,
        withCloseButton: false,
      };

      // Mantine ignores a show() whose id is already on screen, so show() only adds the warning
      // when it is missing, and update() then sets this tick's countdown.
      notifications.show(notification);
      notifications.update(notification);
    },
    onClear: hideWarning,
  });

  useEffect(() => hideWarning, []);

  return null;
}
